import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import Pagination from '@/components/Pagination';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import { EmptyState, ErrorBanner, ExportButtons, FilterSelect, SourceChips, StatusBadge, Toolbar } from '@/components/ui';
import type { ExportParams } from '@/utils/downloadExport';
import { useToast } from '@/components/Toast';
import { useWorkspaceScopedEffect } from '@/hooks/useWorkspaceScopedEffect';
import type { ActivityLog } from '@/types';
import { shortId } from '@/utils/jobStatus';
import { formatApiError } from '@/utils/formatApiError';
import AppConfirmDialog from '@/ui/components/AppConfirmDialog';

type Props = {
  title?: string;
  jobId?: string;
  searchId?: string;
  sourceId?: string;
  limit?: number;
  showFilters?: boolean;
  showClear?: boolean;
  onClear?: () => void;
  compact?: boolean;
  exportPath?: string;
  exportParams?: ExportParams;
  /** When this value changes, data reloads in the background without clearing the table. */
  refreshSignal?: unknown;
};

const KIND_LABELS: Record<string, string> = {
  delivery: 'Delivery',
  fetch_run: 'Fetch run',
  search_execution: 'Pipeline',
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

function statusVariant(status: string | null | undefined): 'success' | 'danger' | 'warning' | 'neutral' | 'info' {
  if (!status) return 'neutral';
  const normalized = status.toLowerCase();
  if (['sent', 'success', 'completed'].includes(normalized)) return 'success';
  if (['failed', 'partial', 'partial_failed', 'dead_letter'].includes(normalized)) return 'danger';
  if (['skipped', 'warning', 'partial_failed'].includes(normalized)) return 'warning';
  return 'neutral';
}

function payloadSourceName(log: ActivityLog): string | null {
  const payload = log.payload ?? {};
  return typeof payload.source_name === 'string' ? payload.source_name : null;
}

function payloadText(log: ActivityLog): string {
  const payload = log.payload ?? {};
  const sourceName = typeof payload.source_name === 'string' ? payload.source_name : null;
  const searchName = typeof payload.search_name === 'string' ? payload.search_name : null;
  const url = typeof payload.url === 'string' ? payload.url : null;
  if (url) return url;
  if (searchName && sourceName) return `${searchName} · ${sourceName}`;
  return sourceName ?? searchName ?? log.message;
}

export default function ActivityLogPanel({
  title = 'Activity log',
  jobId,
  searchId,
  sourceId,
  limit = 10,
  showFilters = false,
  showClear = false,
  onClear,
  compact = false,
  exportPath,
  exportParams,
  refreshSignal,
}: Props) {
  const { toast } = useToast();
  const [items, setItems] = useState<ActivityLog[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearError, setClearError] = useState('');
  const loadedRef = useRef(false);

  useWorkspaceScopedEffect(() => {
    loadedRef.current = false;
    setItems([]);
    setMeta({ pages: 1, total: 0 });
    setPage(1);
    setError('');
  });

  const load = useCallback(async (background = false) => {
    const keepVisible = background && loadedRef.current;
    if (!keepVisible) {
      setLoading(true);
    }
    try {
      const result = await api.activityLogs({
        page,
        limit,
        job_id: jobId,
        search_id: searchId,
        source_id: sourceId,
        kind: kindFilter || undefined,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setMeta({ pages: result.pages, total: result.total });
      loadedRef.current = result.items.length > 0;
      setError('');
    } catch (e) {
      setError(formatApiError(e, 'Failed to load activity'));
    } finally {
      if (!keepVisible) {
        setLoading(false);
      }
    }
  }, [page, limit, jobId, searchId, sourceId, kindFilter, statusFilter]);

  useEffect(() => {
    loadedRef.current = false;
    void load(false);
  }, [load]);

  useEffect(() => {
    if (refreshSignal == null) return;
    void load(true);
  }, [load, refreshSignal]);

  const handleClearScope = useCallback(async () => {
    if (!searchId && !sourceId) return;
    setClearing(true);
    setClearError('');
    try {
      const result = searchId
        ? await api.clearActivityForSearch(searchId)
        : await api.clearActivityForSource(sourceId!);
      toast(`Removed ${result.deleted ?? 0} activity record(s)`, 'success');
      setShowClearConfirm(false);
      onClear?.();
      await load();
    } catch (e) {
      setClearError(formatApiError(e, 'Clear failed'));
    } finally {
      setClearing(false);
    }
  }, [load, onClear, searchId, sourceId, toast]);

  const columns = useMemo<DataTableColumn<ActivityLog>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        width: compact ? 130 : 150,
        render: (log) => (
          <TruncatedCell value={new Date(log.created_at).toLocaleString()} title={log.created_at} />
        ),
      },
      {
        id: 'kind',
        header: 'Type',
        width: 100,
        render: (log) => <TruncatedCell value={kindLabel(log.kind)} />,
      },
      {
        id: 'status',
        header: 'Status',
        width: 90,
        render: (log) =>
          log.status ? (
            <StatusBadge variant={statusVariant(log.status)}>{log.status}</StatusBadge>
          ) : (
            '—'
          ),
      },
      {
        id: 'source',
        header: 'Source',
        width: 120,
        render: (log) => {
          const sourceName = payloadSourceName(log);
          return sourceName ? <SourceChips sources={[sourceName]} variant="compact" /> : '—';
        },
      },
      {
        id: 'message',
        header: 'Message',
        width: compact ? 220 : 280,
        render: (log) => <TruncatedCell value={log.message || payloadText(log)} />,
      },
      {
        id: 'links',
        header: 'Related',
        width: compact ? 160 : 220,
        render: (log) => (
          <span className="activity-log-links">
            {log.job_id ? (
              <Link to={`/jobs?job_id=${log.job_id}`} className="table-link">
                Job {shortId(log.job_id)}
              </Link>
            ) : null}
            {log.search_id ? (
              <>
                {log.job_id ? ' · ' : null}
                <Link to={`/searches/${log.search_id}/links`} className="table-link">
                  Search
                </Link>
              </>
            ) : null}
            {log.source_id ? (
              <>
                {log.job_id || log.search_id ? ' · ' : null}
                <Link to="/sources" className="table-link" title={log.source_id}>
                  Source
                </Link>
              </>
            ) : null}
            {!log.job_id && !log.search_id && !log.source_id ? '—' : null}
          </span>
        ),
      },
    ],
    [compact]
  );

  const resolvedExportParams = useMemo(
    () => ({
      ...exportParams,
      job_id: jobId || exportParams?.job_id,
      search_id: searchId || exportParams?.search_id,
      source_id: sourceId || exportParams?.source_id,
      kind: kindFilter || exportParams?.kind,
      status: statusFilter || exportParams?.status,
    }),
    [exportParams, jobId, searchId, sourceId, kindFilter, statusFilter]
  );

  return (
    <div className="activity-log-panel">
      <div className="activity-log-panel__header">
        <h3>{title}</h3>
        {meta.total > 0 ? <span className="muted">{meta.total} record(s)</span> : null}
        {showClear && (searchId || sourceId) ? (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={clearing}
            onClick={() => {
              setClearError('');
              setShowClearConfirm(true);
            }}
          >
            Clear activity
          </button>
        ) : null}
      </div>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      {showFilters ? (
        <Toolbar>
          <FilterSelect label="Type" htmlFor="activity-log-kind">
            <select
              id="activity-log-kind"
              className="filter-select"
              value={kindFilter}
              onChange={(e) => {
                setKindFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All types</option>
              <option value="delivery">Delivery</option>
              <option value="fetch_run">Fetch run</option>
              <option value="search_execution">Pipeline</option>
            </select>
          </FilterSelect>
          <FilterSelect label="Status" htmlFor="activity-log-status">
            <input
              id="activity-log-status"
              className="filter-select"
              placeholder="e.g. SENT, failed"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            />
          </FilterSelect>
        </Toolbar>
      ) : null}

      {loading && items.length === 0 ? (
        <div className="loading-inline" role="status">
          <span className="spinner" aria-hidden />
          Loading activity…
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            getRowKey={(log) => log.id}
            emptyState={
              <EmptyState
                title="No activity yet"
                description="Pipeline runs, fetch runs, and deliveries linked to this scope appear here."
              />
            }
            columnStorageKey={`activity-log-${jobId ?? searchId ?? sourceId ?? 'all'}`}
            toolbar={{
              onRefresh: () => void load(),
              rightSlot: exportPath ? (
                <ExportButtons path={exportPath} params={resolvedExportParams} />
              ) : undefined,
            }}
          />
          {meta.pages > 1 ? (
            <Pagination page={page} pages={meta.pages} total={meta.total} onPage={setPage} />
          ) : null}
        </>
      )}

      {showClearConfirm ? (
        <AppConfirmDialog
          title={`Clear activity for this ${searchId ? 'search' : 'source'}?`}
          lead="Permanently remove all activity log entries for this scope. Delivery and pipeline records in other views may remain until removed separately."
          confirmLabel="Clear activity"
          submitting={clearing}
          error={clearError}
          onClose={() => {
            if (!clearing) setShowClearConfirm(false);
          }}
          onConfirm={handleClearScope}
        />
      ) : null}
    </div>
  );
}